import { FC, lazy } from 'react';
import { OrganisationDetailsResponse } from 'src/apis/organisation-service/types';
import { PageSpinner } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { useOperatorDetails } from 'src/pages/settings/organisation/company-information/edit-operator-information/use-operator-details';
import { useShipperDetails } from 'src/pages/settings/organisation/company-information/edit-shipper-information/use-shipper-details';
import { useOrganisationDetails } from 'src/pages/settings/organisation/company-information/use-organisation-details';
import { OrganisationServiceRequest } from 'src/pages/settings/organisation/use-organisation-service';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(
  () => import('src/pages/settings/organisation/company-information/page')
);

type Props = {
  organisationService: Successful<OrganisationServiceRequest>;
};

export const CompanyInformationOverview: FC<Props> = ({
  organisationService,
}) => {
  if (!organisationService.response.data._links.getMyOrganisationDetails)
    throw new Forbidden();

  const organisationDetails = useOrganisationDetails({
    url: organisationService.response.data._links.getMyOrganisationDetails.href,
  });
  const operatorDetails = useOperatorDetails({ organisationDetails });
  const shipperDetails = useShipperDetails({ organisationDetails });

  const operatorDetailsNeededAndNotLoadedYet =
    organisationDetails.response &&
    hasOperatorDetails(organisationDetails.response.data) &&
    !operatorDetails.response;

  const shipperDetailsNeededAndNotLoadedYet =
    organisationDetails?.response?.data._links.shipperDetails &&
    !shipperDetails.response;

  if (
    !organisationDetails.response ||
    operatorDetailsNeededAndNotLoadedYet ||
    shipperDetailsNeededAndNotLoadedYet
  )
    return <PageSpinner />;

  return (
    <Page
      organisationDetails={organisationDetails}
      operatorDetails={operatorDetails}
      shipperDetails={shipperDetails}
    />
  );
};

export function hasOperatorDetails(resp: OrganisationDetailsResponse) {
  return resp._links.tsoDetails || resp._links.ssoDetails;
}
